import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Shield, FileText, Loader2, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

// localStorage cache key — when present, KVKK modal stays closed even if
// the backend round-trip is slow/failing. Refreshed every time the user
// updates consents.
const KVKK_LOCAL_FLAG = 'kvkk_consent_v1';
// CookieBanner cache key — keep in sync so accepting the GDPR modal also
// hides the cookie banner (and vice versa). Same shape as CookieBanner.jsx.
const COOKIE_CONSENT_KEY = 'cookie_consent_v1';

const writeLocalConsentFlags = (consentData) => {
  try {
    const now = new Date().toISOString();
    localStorage.setItem(KVKK_LOCAL_FLAG, JSON.stringify({
      accepted: true,
      categories: consentData,
      timestamp: now,
    }));
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      essential: true,
      analytics: !!consentData.cerez_analitik,
      marketing: !!consentData.pazarlama_iletisim,
      timestamp: now,
    }));
  } catch (_) { /* localStorage may be unavailable in private mode */ }
};

const KVKKConsentModal = ({ user, onConsentsAccepted }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState({});
  const [texts, setTexts] = useState([]);
  const [consents, setConsents] = useState({});
  const [expandedText, setExpandedText] = useState(null);

  const checkConsents = useCallback(async () => {
    if (!user) return;
    // Fast-path: if the local flag exists, skip the modal entirely. This
    // protects against slow/failing /kvkk/my-consents calls AND the user
    // race condition where the cached response says "not accepted" right
    // after a successful save (eventual consistency).
    try {
      const cached = localStorage.getItem(KVKK_LOCAL_FLAG);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.accepted) {
          setLoading(false);
          return;
        }
      }
    } catch (_) { /* ignore */ }
    try {
      const res = await axios.get(`${API}/kvkk/my-consents`);
      setCategories(res.data.categories || {});
      if (!res.data.all_accepted) {
        setOpen(true);
        // Current rıza durumlarını yükle
        const existing = res.data.consents || {};
        const initial = {};
        Object.keys(res.data.categories || {}).forEach(key => {
          initial[key] = existing[key]?.accepted || false;
        });
        setConsents(initial);
      } else {
        // Backend confirms acceptance → refresh local cache.
        const map = {};
        Object.entries(res.data.consents || {}).forEach(([k, v]) => {
          map[k] = !!v?.accepted;
        });
        writeLocalConsentFlags(map);
      }
    } catch (error) {
      // 401 = kullanıcı giriş yapmamış, normal
      if (error.response?.status !== 401) {
        console.error('KVKK consent check failed:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchTexts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/kvkk/texts`);
      setTexts(res.data.texts || []);
    } catch (error) {
      console.error('KVKK texts fetch failed:', error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      checkConsents();
      fetchTexts();
    }
  }, [user, checkConsents, fetchTexts]);

  const handleAcceptRequired = async () => {
    // Required olanları kabul et
    const requiredConsents = {};
    Object.entries(categories).forEach(([key, info]) => {
      if (info.required) {
        requiredConsents[key] = true;
      }
    });
    // Optional tercihleri de dahil et
    Object.entries(consents).forEach(([key, value]) => {
      if (!categories[key]?.required) {
        requiredConsents[key] = value;
      }
    });
    await submitConsents(requiredConsents);
  };

  const handleAcceptAll = async () => {
    const allConsents = {};
    Object.keys(categories).forEach(key => {
      allConsents[key] = true;
    });
    await submitConsents(allConsents);
  };

  const submitConsents = async (consentData) => {
    setSaving(true);
    try {
      await axios.post(`${API}/kvkk/give-consent`, {
        consents: consentData,
        version: "1.0",
      });
      // Persist a local flag so the modal stays closed across reloads
      // even if a future /kvkk/my-consents call lags behind.
      writeLocalConsentFlags(consentData);
      toast.success('Your GDPR consents have been saved');
      setOpen(false);
      if (onConsentsAccepted) onConsentsAccepted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save consents');
    } finally {
      setSaving(false);
    }
  };

  const requiredNotAccepted = Object.entries(categories).some(
    ([key, info]) => info.required && !consents[key]
  );

  const getTextForCategory = (categoryKey) => {
    return texts.find(t => t.category === categoryKey);
  };

  if (!user || loading || !open) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-900">
            <Shield className="w-5 h-5" />
            Personal Data Protection
          </DialogTitle>
          <DialogDescription>
            Your consent is required under UK GDPR / UK DPA 2018.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto max-h-[50vh] pr-2" data-testid="kvkk-scroll-area">
          <div className="space-y-4 py-2">
            {/* Required Consents */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Required Consents</p>
              {Object.entries(categories)
                .filter(([, info]) => info.required)
                .map(([key, info]) => {
                  const text = getTextForCategory(key);
                  return (
                    <div key={key} className="border rounded-lg p-3 mb-2" data-testid={`consent-${key}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={consents[key] || false}
                          onCheckedChange={(checked) => setConsents(prev => ({ ...prev, [key]: !!checked }))}
                          id={`consent-${key}`}
                          data-testid={`consent-checkbox-${key}`}
                        />
                        <div className="flex-1">
                          <label htmlFor={`consent-${key}`} className="text-sm font-medium cursor-pointer">
                            {info.label} <span className="text-red-500">*</span>
                          </label>
                          <p className="text-xs text-slate-500 mt-0.5">{info.description}</p>
                          {text && (
                            <button
                              onClick={() => setExpandedText(expandedText === key ? null : key)}
                              className="flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 mt-1"
                            >
                              <FileText className="w-3 h-3" />
                              Read Text
                              {expandedText === key ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            </button>
                          )}
                          {expandedText === key && text && (
                            <div className="mt-2 p-3 bg-slate-50 rounded text-xs text-slate-700 max-h-40 overflow-y-auto leading-relaxed whitespace-pre-line">
                              {text.content?.slice(0, 2000)}
                              {text.content?.length > 2000 && (
                                <a href={`/page/${text.slug}`} className="block mt-2 text-emerald-700 underline" target="_blank" rel="noreferrer">
                                  Read Full <ExternalLink className="w-3 h-3 inline" />
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Optional Consents */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Optional Preferences</p>
              {Object.entries(categories)
                .filter(([, info]) => !info.required)
                .map(([key, info]) => (
                  <div key={key} className="border rounded-lg p-3 mb-2" data-testid={`consent-${key}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={consents[key] || false}
                        onCheckedChange={(checked) => setConsents(prev => ({ ...prev, [key]: !!checked }))}
                        id={`consent-${key}`}
                        data-testid={`consent-checkbox-${key}`}
                      />
                      <div className="flex-1">
                        <label htmlFor={`consent-${key}`} className="text-sm font-medium cursor-pointer">
                          {info.label}
                        </label>
                        <p className="text-xs text-slate-500 mt-0.5">{info.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            <p className="text-[10px] text-slate-400 italic">
              * These texts are drafts and do not constitute legal advice.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            onClick={handleAcceptAll}
            disabled={saving}
            className="w-full bg-emerald-800 hover:bg-emerald-900"
            data-testid="kvkk-accept-all"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
            Accept All
          </Button>
          <Button
            variant="outline"
            onClick={handleAcceptRequired}
            disabled={saving || requiredNotAccepted}
            className="w-full"
            data-testid="kvkk-accept-required"
          >
            Continue with Selected Consents
          </Button>
          {requiredNotAccepted && (
            <p className="text-xs text-red-500 text-center">
              You must accept the required consents.
            </p>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KVKKConsentModal;
